import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Building2, Users, Briefcase, TrendingUp, Settings, CreditCard } from 'lucide-react';

export default function OrgOwnerDashboard() {
  const { user } = useAuth();

  const stats = [
    { label: 'Active Jobs', value: '12', change: '+2 this week', icon: Briefcase, color: 'text-blue-600' },
    { label: 'Team Members', value: '8', change: '3 pending invites', icon: Users, color: 'text-green-600' },
    { label: 'Total Applications', value: '145', change: '+23 this week', icon: TrendingUp, color: 'text-purple-600' },
    { label: 'Plan Status', value: 'Pro', change: 'Next billing: Dec 15', icon: CreditCard, color: 'text-orange-600' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Organization Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Welcome back, {user?.firstName}! Manage your organization and team.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </button>
            <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
              <Users className="w-4 h-4 mr-2" />
              Invite Team
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-lg bg-gray-50 ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600">{stat.label}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="mt-2 text-xs text-gray-500">{stat.change}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors">
              <Briefcase className="w-8 h-8 text-primary-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Post New Job</p>
                <p className="text-sm text-gray-500">Create a job posting</p>
              </div>
            </button>

            <button className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors">
              <Users className="w-8 h-8 text-primary-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Manage Team</p>
                <p className="text-sm text-gray-500">Add or remove members</p>
              </div>
            </button>

            <button className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors">
              <Building2 className="w-8 h-8 text-primary-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Company Profile</p>
                <p className="text-sm text-gray-500">Update organization info</p>
              </div>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {[
              { action: 'New application received', job: 'Senior Software Engineer', time: '2 hours ago' },
              { action: 'Team member invited', job: 'john.doe@company.com', time: '5 hours ago' },
              { action: 'Job posted', job: 'Product Manager', time: '1 day ago' },
              { action: 'Application shortlisted', job: 'UX Designer', time: '2 days ago' },
            ].map((activity, index) => (
              <div key={index} className="flex items-start pb-4 border-b border-gray-100 last:border-0">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-sm text-gray-600">{activity.job}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Organization Info */}
        {user?.company && (
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg border border-primary-200 p-6">
            <div className="flex items-start">
              <Building2 className="w-8 h-8 text-primary-600 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{user.company}</h3>
                <p className="text-sm text-gray-600 mt-1">Organization Owner</p>
                {user.orgId && (
                  <p className="text-xs text-gray-500 mt-2">
                    Org ID: {user.orgId}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
